import React, { useState, useEffect } from 'react'
import { validatePasswordToken, setPassword } from '@lib/spektrix/customer'
import { validatePassword, validatePasswordMatch } from '@lib/validation'
import PasswordInput from '../shared/password-input'

const ChangePassword = ({ token }) => {
  const [isValidatingToken, setIsValidatingToken] = useState(true)
  const [isTokenValid, setIsTokenValid] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false)
        setIsValidatingToken(false)
        return
      }

      try {
        const result = await validatePasswordToken(token)
        setIsTokenValid(result.valid)
      } catch (error) {
        console.error('Token validation failed:', error)
        setIsTokenValid(false)
      } finally {
        setIsValidatingToken(false)
      }
    }

    checkToken()
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    // Validate password
    const passwordValidation = validatePassword(newPassword)
    if (!passwordValidation.valid) {
      setErrors({ newPassword: passwordValidation.errors[0] })
      return
    }

    // Check passwords match
    if (!validatePasswordMatch(newPassword, confirmPassword)) {
      setErrors({ confirmPassword: 'Passwords do not match' })
      return
    }

    setIsLoading(true)

    try {
      await setPassword(token, newPassword)
      setSuccess(true)
    } catch (error) {
      console.error('Password change failed:', error)
      setErrors({
        submit: error.message || 'Failed to change password. Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Loading state while validating token
  if (isValidatingToken) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 mx-auto text-blue-600 dark:text-blue-400 mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-600 dark:text-gray-400">Validating reset link...</p>
        </div>
      </div>
    )
  }

  // Invalid token state
  if (!isTokenValid) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400 dark:text-red-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Invalid or expired reset link
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>
                  This password reset link is invalid or has expired.
                  Please request a new password reset link.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <a
            href="/reset-password"
            className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors"
          >
            Request New Reset Link
          </a>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400 dark:text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Password changed successfully
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>
                  Your password has been changed. You can now sign in with your new password.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <a
            href="/my-account"
            className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors"
          >
            Sign In
          </a>
        </div>
      </div>
    )
  }

  // Password change form
  return (
    <div>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Please enter your new password below.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.submit && (
          <div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
          </div>
        )}

        <PasswordInput
          id="newPassword"
          label="New Password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value)
            if (errors.newPassword) {
              setErrors({ ...errors, newPassword: null })
            }
          }}
          placeholder="Enter new password"
          showStrengthIndicator={true}
          required={true}
          disabled={isLoading}
          error={errors.newPassword}
        />

        <PasswordInput
          id="confirmPassword"
          label="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value)
            if (errors.confirmPassword) {
              setErrors({ ...errors, confirmPassword: null })
            }
          }}
          placeholder="Confirm new password"
          required={true}
          disabled={isLoading}
          error={errors.confirmPassword}
        />

        <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
            Password requirements:
          </p>
          <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
            <li>At least 8 characters long</li>
            <li>Contains at least one uppercase letter</li>
            <li>Contains at least one lowercase letter</li>
            <li>Contains at least one number</li>
          </ul>
        </div>

        <button
          type="submit"
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Changing Password...
            </span>
          ) : (
            'Change Password'
          )}
        </button>
      </form>
    </div>
  )
}

export default ChangePassword
